import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SentryService } from '@shared-lib/services/sentry.service';
import { AuthService } from '@shared/services/auth.service';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

@Injectable()
export class DashlyErrorInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly sentryService = inject(SentryService);

  private readonly MAX_RETRIES = 4;
  private readonly RETRY_DELAY = 2000;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      retry({
        count: this.MAX_RETRIES,
        delay: (error: HttpErrorResponse) => {
          if (error?.status === 0) {
            return timer(this.RETRY_DELAY);
          }
          throw error;
        },
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 0 && error.status !== 401 && error.status !== 503) {
          this.sentryService.captureHttpError(req, error, this.authService.userIdFromToken());
        }

        if (error.status === 401 && (this.isInvalidTokenError(error) || !this.isAuthServiceRequest(req))) {
          this.authService.logoutAndRedirect();
        }

        // FIXME: for some reason BE returns 403 when token is invalid/expired
        if (error.status === 403) {
          this.authService.logoutAndRedirect();
        }

        return throwError(() => error);
      }),
    );
  }

  private isInvalidTokenError(error: HttpErrorResponse): boolean {
    const message: string = error.error?.message ?? '';

    return (
      message.includes('JWT_TOKEN__EXPIRED') ||
      message.includes('JWT_TOKEN__INVALID_APPLICATION') ||
      message.includes('JWT_TOKEN__INVALID_TOKEN')
    );
  }

  private isAuthServiceRequest(req: HttpRequest<unknown>): boolean {
    return req.url.includes('/auth-service/');
  }
}
